/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/LaneShell.tsx
 * FILE_ID: EMAILHUB-READERHEADER-LANES-SHELL-001
 * PURPOSE: One bounded lane cell — label, wrapping chip run, reserved ellipsis.
 * ROLE IN ARCHITECTURE: Presentational shell shared by the four lane leaves.
 * MAIN EXPORTS: LaneShell, LANE_TOKEN_KEY.
 * NON-RESPONSIBILITIES: No lane content decisions, no menu state.
 * NOTES FOR FUTURE AI: Parent grid separates lanes with gap (change #430),
 *   not per-cell borders — never grow nested bordered cards here (plan
 *   glitch list). The ellipsis slot is ALWAYS rendered (touch discoverability,
 *   no layout shift). min-w-0 + overflow-hidden is the no-horizontal-scroll
 *   defence.
 *
 *   ONE ROW, not two (change #294). The caption used to own a full row above
 *   the chips, so each lane cost ~28px of mostly-blank space before it said
 *   anything — four lanes, ~56px of nothing, which is what the operator meant
 *   by "there is so much empty space". The lanes are the resting state now, so
 *   that space is charged against the message body on every email.
 *
 *   ONE ROW now means exactly one (change #302): chips TRUNCATE, they do not
 *   wrap. Wrapping let a busy lane silently become two or three rows, so the
 *   strip's height changed per email and the operator read it as clutter
 *   ("so cluttered ugly", 2026-07-25). Overflow belongs in the lane menu, not
 *   in extra rows. Never restore flex-wrap here.
 *
 *   The chip run is shrink, not flex-1 (change #325) — pairs with the
 *   content-sized grid tracks in ExpandedContext. Either one alone puts the
 *   gutter back.
 */
import type { ReactNode } from 'react'

import { EMAIL_SIGNAL_LANE_TOKENS, type EmailSignalLane } from '@quotech/ui'
import { cn } from '../../../_internal/lib/utils'
import { EllipsisSlot } from '../menus/EllipsisSlot'
import type { LaneSurfaceProps } from '../menus/useContextMenuRouting'
import type { ReaderContextLaneId, ReaderTriageLaneId } from '../menus/menuScopes'

/** Triage lane id → legacy visual-token key (labels + hue classes).
 *  Workflow lanes never render through LaneShell (WorkflowRow owns them). */
export const LANE_TOKEN_KEY: Readonly<Record<ReaderTriageLaneId, EmailSignalLane>> = {
  company: 'who',
  classification: 'what',
  intelligence: 'signals',
  filedTo: 'where',
}

export interface LaneShellProps {
  lane: ReaderTriageLaneId
  /** Routing owner's surface props (context menu + keyboard). */
  surfaceProps: LaneSurfaceProps
  onOpenEllipsis: (lane: ReaderContextLaneId, trigger: HTMLElement) => void
  children: ReactNode
  /** Rendered dimmed after the chip run (e.g. empty affordances). */
  trailing?: ReactNode
  className?: string
  /**
   * Render for a container that has height to spare (the compact chip
   * popover), letting the chip run wrap instead of truncate.
   *
   * This is the ONE exception to the never-wrap rule above, and it does not
   * reopen change #302. That change was about the STRIP: wrapping there made
   * the resting header taller or shorter per email, so the body moved as the
   * operator paged through the list. A popover is opened deliberately, sized
   * to its own content and closes again, so its height is not charged to
   * anything. Truncating inside it would be the actual bug — it is the only
   * place a narrow reader can reach these controls, so a clipped chip is an
   * unreachable control. Never default this to true.
   */
  expand?: boolean
}

/**
 * Frame for one semantic lane: label row, wrapping content, ellipsis slot.
 *
 * Inputs: lane id, routing surface props, chip children.
 * Output: a focusable region (`tabIndex 0`) so Shift+F10 works from the
 * bare lane surface, with `data-reader-lane` for scope resolution.
 * Failure mode: none — an empty children set still shows label + ellipsis.
 */
export function LaneShell({
  lane,
  surfaceProps,
  onOpenEllipsis,
  children,
  trailing,
  className,
  expand = false,
}: LaneShellProps) {
  const token = EMAIL_SIGNAL_LANE_TOKENS[LANE_TOKEN_KEY[lane]]
  return (
    <section
      {...surfaceProps}
      tabIndex={0}
      aria-label={`${token.label} lane`}
      data-testid={`reader-lane-${lane}`}
      className={cn(
        // gap-1 inside, against the parent grid's gap-x-6 between lanes.
        // The ratio is the grouping: a caption sits nearer its own chips than
        // to the neighbouring lane, so the boundary is legible without the
        // borders that change #430 removed. Tightening this also buys back
        // part of the width the wider outer gap spends.
        //
        // py-1, not py-1.5: py-1 (8px) is the number CONTEXT_LANES_MAX is
        // derived from ("py-1 (8) + the 24px ellipsis slot = ~32px" in
        // tokens.ts). py-1.5 quietly spent 4px a row more than the budget it
        // was measured against, on the tier where two rows are stacked.
        'group flex min-w-0 gap-1 px-0.5 py-1 outline-none',
        // Expanded runs can be several lines tall; the caption belongs beside
        // the FIRST line, not floating in the middle of the block.
        expand ? 'items-start' : 'items-center',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
        className,
      )}
    >
      {/* shortLabel, not label: "CLASSIFICATION"/"INTELLIGENCE" spent more
          width on the caption than the chips it introduces. aria-label above
          still announces the full lane name.

          Intrinsic width, NOT the old fixed w-[3.25rem] column. That column
          existed to left-align four captions onto one vertical line, which
          only paid off while all four lanes shared a row. It costs a visible
          void after every caption shorter than 52px — "CLASS." and "INTEL."
          are ~34px, so the operator read a ~18px gap between the caption and
          the chip it introduces (2026-08-13). The tier that kept the four-lane
          row (expanded) puts each caption at the start of its own grid track,
          so they still line up without reserving the width. */}
      <span
        className={cn(
          'shrink-0 select-none whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground',
          // A stacked row shares one label column so Company, Class, Intel,
          // and Filed start their chips on the same line. w-16 fits COMPANY,
          // the longest caption. The desktop one-row tier stays content-sized.
          expand && 'w-16',
          token.labelClass,
        )}
      >
        {token.shortLabel}
      </span>
      {/* ONE ROW: chips truncate, they never wrap onto a second line. Wrapping
          made lane height depend on content, so the strip grew and shrank as
          the operator moved between emails. overflow-hidden (never -auto) keeps
          the no-nested-scroller rule. */}
      {/* shrink, NOT flex-1 (change #325). flex-1 stretched this box across the
          whole lane, which parked the ellipsis on the far right and left the
          single chip floating alone in a wide empty box. Sized to content, the
          lane reads as one dense run: caption, chips, menu. It still shrinks
          and truncates under pressure, and the ellipsis stays outside the
          overflow-hidden box so the escape hatch is never the thing clipped. */}
      <div
        className={cn(
          'flex min-w-0 items-center gap-1',
          expand
            ? 'flex-1 flex-wrap gap-y-1'
            : 'shrink flex-nowrap overflow-hidden',
        )}
      >
        {children}
        {trailing}
      </div>
      <EllipsisSlot
        label={`${token.label} actions`}
        onOpen={(trigger) => onOpenEllipsis(lane, trigger)}
      />
    </section>
  )
}
